<template>
	<div
		v-show="showAlert"
		role="alert"
		class="alert-message sgnl-flex sgnl-align-start sgnl-gap-16 sgnl-paragraph-s-regular"
		:class="[`alert-message--${resolvedType}`, `sgnl-text-color-${resolvedType}`]"
	>
		<component
			:is="icon"
			class="sgnl-flex-shrink-0 sgnl-icon-align-text"
			:class="`sgnl-icon-color-${resolvedType}`"
		/>
		<div class="alert-message__column">
			<div class="alert-message__content">
				<slot />
			</div>
			<div
				v-if="$slots.action"
				class="alert-message__action"
			>
				<slot name="action" />
			</div>
		</div>
		<v-btn
			v-if="closable"
			icon
			variant="text"
			size="small"
			aria-label="Dismiss"
			@click="$emit('close')"
		>
			<Close16 />
		</v-btn>
	</div>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType, type Component } from "vue";

import Close16 from "@carbon/icons-vue/es/close/16";
import ErrorOutline16 from "@carbon/icons-vue/es/error--outline/16";
import Warning16 from "@carbon/icons-vue/es/warning/16";
import Information16 from "@carbon/icons-vue/es/information/16";
import CheckmarkOutline16 from "@carbon/icons-vue/es/checkmark--outline/16";

type AlertType = "success" | "info" | "warning" | "error";

const TYPE_ICON = {
	success: markRaw(CheckmarkOutline16),
	info: markRaw(Information16),
	warning: markRaw(Warning16),
	error: markRaw(ErrorOutline16),
} as Record<AlertType, Component>;

export default defineComponent({
	name: "AlertMessage",
	components: { Close16 },
	props: {
		type: { type: String as PropType<AlertType | "danger">, default: "info" },
		showAlert: { type: Boolean, default: true },
		closable: { type: Boolean, default: false },
	},
	emits: ["close"],
	computed: {
		resolvedType(): AlertType {
			return this.type === "danger" ? "error" : this.type;
		},
		icon(): Component {
			return TYPE_ICON[this.resolvedType];
		},
	},
});
</script>

<style lang="scss" scoped>
.alert-message {
	padding: var(--sgnl-space-12) var(--sgnl-space-24);
	border: var(--sgnl-border-width-1) solid;
	border-radius: var(--sgnl-border-radius-6);
}

.alert-message--success {
	border-color: var(--sgnl-alert-success-border);
	background: var(--sgnl-alert-success-background-primary);
}
.alert-message--info {
	border-color: var(--sgnl-alert-info-border);
	background: var(--sgnl-alert-info-background-primary);
}
.alert-message--warning {
	border-color: var(--sgnl-alert-warning-border);
	background: var(--sgnl-alert-warning-background-primary);
}
.alert-message--error {
	border-color: var(--sgnl-alert-error-border);
	background: var(--sgnl-alert-error-background-primary);
}

.alert-message__column {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.alert-message__content {
	min-width: 0;
}
</style>
